import { api } from "@/services/api"

import type {
  AdminDailyStatsResponse,
  AdminHealthResponse,
  AdminListOptions,
  AdminListResponse,
  AdminResource,
  AdminResourcesResponse,
  AdminSingleResponse,
  AdminUsersViewParams,
  AdminUserViewResponse,
  HandoffDailyStatsResponse,
  SchedulerStatsResponse,
  SchedulerUpcomingResponse,
  ToolCatalogItem,
  ToolTutorial,
  WhatsAppDailyStatsResponse,
} from "../types"

const ADMIN_BASE_URL = "/admin"

export class AdminApiService {
  private baseURL = ADMIN_BASE_URL

  constructor() {
    // O interceptor de auth já está configurado no api base
  }

  private buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          searchParams.append(key, value.join(","))
        } else {
          searchParams.append(key, String(value))
        }
      }
    })

    return searchParams.toString()
  }

  /**
   * Obtém todos os recursos disponíveis
   */
  async getResources(): Promise<AdminResourcesResponse> {
    const response = await api.get(`${this.baseURL}/resources`)
    return response.data
  }

  /**
   * Obtém metadados de um recurso específico
   */
  async getResourceMetadata(resource: string): Promise<AdminResource> {
    const response = await api.get(`${this.baseURL}/resources/${resource}/metadata`)
    return response.data
  }

  /**
   * Lista recursos com filtros opcionais
   */
  async listResource<T>(
    resource: string,
    options: AdminListOptions = {}
  ): Promise<AdminListResponse<T>> {
    const queryString = this.buildQueryString(options)
    const url = queryString
      ? `${this.baseURL}/resources/${resource}?${queryString}`
      : `${this.baseURL}/resources/${resource}`

    const response = await api.get(url)
    return response.data
  }

  /**
   * Obtém um recurso específico por ID
   */
  async getResource<T>(resource: string, id: string): Promise<AdminSingleResponse<T>> {
    const response = await api.get(`${this.baseURL}/resources/${resource}/${id}`)
    return response.data
  }

  /**
   * Cria um novo recurso
   */
  async createResource<T>(resource: string, data: Partial<T>): Promise<AdminSingleResponse<T>> {
    const response = await api.post(`${this.baseURL}/resources/${resource}`, { data })
    return response.data
  }

  /**
   * Atualiza um recurso completamente (PUT)
   */
  async updateResource<T>(
    resource: string,
    id: string,
    data: Partial<T>
  ): Promise<AdminSingleResponse<T>> {
    const response = await api.put(`${this.baseURL}/resources/${resource}/${id}`, { data })
    return response.data
  }

  /**
   * Atualiza um recurso parcialmente (PATCH)
   */
  async patchResource<T>(
    resource: string,
    id: string,
    data: Partial<T>
  ): Promise<AdminSingleResponse<T>> {
    const response = await api.patch(`${this.baseURL}/resources/${resource}/${id}`, { data })
    return response.data
  }

  /**
   * Remove um recurso
   */
  async deleteResource(resource: string, id: string): Promise<void> {
    await api.delete(`${this.baseURL}/resources/${resource}/${id}`)
  }

  /**
   * Lista de usuários admin (materialized view)
   */
  async getAdminUsersView(params: AdminUsersViewParams = {}): Promise<AdminUserViewResponse> {
    const queryString = this.buildQueryString(params)
    const url = queryString
      ? `${this.baseURL}/views/users?${queryString}`
      : `${this.baseURL}/views/users`
    const response = await api.get(url)
    return response.data
  }

  async getUserLastActivity(userId: string): Promise<{ last_activity: string | null }> {
    const response = await api.get(`${this.baseURL}/views/users/${userId}/last-activity`)
    return response.data
  }

  /**
   * Obtém estatísticas diárias de crescimento
   */
  async getDailyStats(days: number = 30): Promise<AdminDailyStatsResponse> {
    const response = await api.get(`${this.baseURL}/views/users/daily-stats?days=${days}`)
    return response.data
  }

  async getWhatsAppDailyStats(days: number = 30): Promise<WhatsAppDailyStatsResponse> {
    const response = await api.get(`${this.baseURL}/views/whatsapp/daily-stats?days=${days}`)
    return response.data
  }

  async getHandoffDailyStats(days: number = 30): Promise<HandoffDailyStatsResponse> {
    const response = await api.get(`${this.baseURL}/views/handoffs/daily-stats?days=${days}`)
    return response.data
  }

  async getSchedulerHistory(period: string = "24h"): Promise<SchedulerStatsResponse> {
    const response = await api.get(`${this.baseURL}/views/schedulers/history?period=${period}`)
    return response.data
  }

  async getSchedulerUpcoming(period: string = "24h"): Promise<SchedulerUpcomingResponse> {
    const response = await api.get(`${this.baseURL}/views/schedulers/upcoming?period=${period}`)
    return response.data
  }

  // --- Prompt Template Versioning ---

  async listPromptVersions(name: string): Promise<
    {
      id: string
      version: number
      is_current: boolean
      is_active: boolean
      created_at: string
      updated_at?: string
    }[]
  > {
    const response = await api.get(`${this.baseURL}/prompt-templates/${name}/versions`)
    return response.data
  }

  async createPromptVersion(
    name: string
  ): Promise<{ id: string; name: string; version: number; is_current: boolean }> {
    const response = await api.post(`${this.baseURL}/prompt-templates/${name}/versions`)
    return response.data
  }

  async setCurrentPromptVersion(
    name: string,
    version: number
  ): Promise<{ name: string; version: number; is_current: boolean }> {
    const response = await api.post(
      `${this.baseURL}/prompt-templates/${name}/versions/${version}/set-current`
    )
    return response.data
  }

  /**
   * Verifica o status de saúde da API Admin
   */
  async healthCheck(): Promise<AdminHealthResponse> {
    const response = await api.get(`${this.baseURL}/health`)
    return response.data
  }

  /**
   * Flags de runtime da plataforma (somente leitura), ex.: override global de debounce.
   */
  async getRuntimeConfig(): Promise<{ message_debounce_enabled: boolean }> {
    const response = await api.get(`${this.baseURL}/runtime-config`)
    return response.data
  }

  /**
   * Catálogo de ferramentas de agente, com disponibilidade calculada por organização.
   * Servido pelo módulo ai-agent (fora do prefixo /admin).
   */
  async getToolCatalog(organizationId?: string): Promise<ToolCatalogItem[]> {
    const response = await api.get("/ai-agent/tools", {
      params: organizationId ? { organization_id: organizationId } : undefined,
    })
    return response.data
  }

  /**
   * Tutorial/FAQ (Markdown) de uma ferramenta de agente.
   */
  async getToolTutorial(toolName: string): Promise<ToolTutorial> {
    const response = await api.get(`/ai-agent/tools/${toolName}/tutorial`)
    return response.data
  }
}

// Instância singleton do serviço
export const adminApiService = new AdminApiService()
